import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { ADMIN_DEFAULT_VISIBLE_FILTERS, ADMIN_DISPUTE_COLUMNS } from "../constants/columns"
import { ADMIN_DISPUTES_SLICE_NAME as SLICE_NAME } from "./adminDisputes.constants"
import { getAdminDisputes, getAdminDisputesExport, pinAdminDispute } from "./adminDisputes.thunks"

/* ------------- Initial State ------------- */

// Checkbox chips carry "all" (or null) while nothing is picked and an array once something
// is — the shape DropdownBlockCheckbox emits. Tag chips carry an array, number and date
// chips null. The query builder drops every one of those empty shapes.
export const DEFAULT_FILTERS = {
  status: "all",
  dispute_type: "all",
  submission: "all",
  flag_reason: "all",
  reason_category: "all",
  reason_code: "all",
  issuer: "all",
  issuing_bank: "all",
  card_type: "all",
  currency: "all",
  card_country: "all",
  processor: "all",
  assignee: "all",
  paid_status: "all",
  customer: [],
  card_last4: [],
  processor_dispute_id: [],
  user_full_name: [],
  user_email: [],
  business_account: [],
  business_url: [],
  exclude_fields: [],
  amount: null,
  enrichment: null,
  date: null,
  updated: null
}

const initialState = {
  fetching: false,
  result: { count: 0, results: [] },
  // Id of the in-flight list request: thunks are not cancelled, so a late response from a
  // superseded request is dropped by comparing this rather than by takeLatest.
  requestId: null,

  defaultColumns: [...ADMIN_DISPUTE_COLUMNS],
  columns: [...ADMIN_DISPUTE_COLUMNS],

  filters: { ...DEFAULT_FILTERS },
  visibleFilters: { ...ADMIN_DEFAULT_VISIBLE_FILTERS },

  exportFetching: false,
  exportResult: null
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setProp(state, { payload: { key, value } }) {
      if (["filters", "visibleFilters"].includes(key)) {
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearFilters(state) {
      state.filters = { ...DEFAULT_FILTERS }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminDisputes.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId
      })
      .addCase(getAdminDisputes.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return
        state.fetching = false
        state.result = {
          count: action.payload?.count || 0,
          results: action.payload?.results || []
        }
      })
      .addCase(getAdminDisputes.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return
        state.fetching = false
      })

      // The row is patched in place so the pin icon flips immediately; the page refetches
      // right after, which is what actually moves the row to the top.
      .addCase(pinAdminDispute.fulfilled, (state, { payload: { id, pinned } }) => {
        state.result.results = state.result.results.map((row) =>
          row.id === id ? { ...row, pinned } : row
        )
      })

      .addCase(getAdminDisputesExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getAdminDisputesExport.fulfilled, (state, { payload }) => {
        state.exportFetching = false
        state.exportResult = payload
      })
      .addCase(getAdminDisputesExport.rejected, (state) => {
        state.exportFetching = false
      })
  }
})

export const { setProp, clearFilters } = slice.actions

/* ------------- Selectors ------------- */

const selectSlice = (state) => state[SLICE_NAME]

export const selectAdminDisputesList = (state) => selectSlice(state).result
export const selectAdminDisputesFetching = (state) => selectSlice(state).fetching
export const selectAdminDisputesFilters = (state) => selectSlice(state).filters
export const selectAdminDisputesVisibleFilters = (state) => selectSlice(state).visibleFilters
export const selectAdminDisputesColumns = (state) => selectSlice(state).columns
export const selectAdminDisputesExport = (state) => ({
  fetching: selectSlice(state).exportFetching,
  result: selectSlice(state).exportResult
})

// "Nothing is narrowing the list" — what the Reset all button keys off. Every chip's empty
// shape counts as untouched.
export const selectAreFiltersDefault = (state) => {
  const filters = selectSlice(state).filters
  return Object.keys(DEFAULT_FILTERS).every((key) => {
    const value = filters[key]
    if (Array.isArray(value)) return value.length === 0
    if (value && typeof value === "object") return !value.type && !value[0] && !value[1]
    return value == null || value === "all"
  })
}

/* ------------- Persistence ------------- */

// Bumped when the column registry changes: the migration rebuilds persisted state from the
// current defaults, so an admin who already opened the page still receives new columns.
const PERSISTED_REDUCER_VERSION = 1

export const reducer = persistReducer(
  {
    key: SLICE_NAME,
    storage,
    version: PERSISTED_REDUCER_VERSION,
    // Transient request state must never come back from storage as "still loading".
    blacklist: ["fetching", "requestId", "exportFetching", "exportResult", "result"],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return { ...state, ...initialState }
      }
      return state
    }
  },
  slice.reducer
)
